// Pluralizzazione ICU (task #171, nodo «plurali»): una foglia del dizionario
// può essere un OGGETTO di categorie CLDR ({ one, few, other, ... }) invece di
// una stringa — t(key, count) sceglie la forma con Intl.PluralRules (nativo,
// zero dipendenze: ci serve la CATEGORIA, non l'intero messageformat ICU).
// Le chiavi non plurali restano stringhe pure: nessuna migrazione di massa.
//
// Ogni locale dichiara ESATTAMENTE le categorie che Intl.PluralRules prevede
// per la sua lingua (l'italiano one/many/other, il polacco one/few/many/other,
// il lettone zero/one/other...): la guardia in test/i18n-dictionaries.test.ts
// fa CI rossa sia sulla forma mancante sia su quella inutile. Dove due
// categorie coincidono in superficie (es. il tedesco al «many» dei milioni)
// il testo si RIPETE: la completezza è ciò che rende il controllo possibile.

export type PluralForms = { [category in Intl.LDMLPluralRule]?: string } & { other: string }

// Identità con excess-property check: nel file di sezione la foglia sorgente
// si scrive plural({ one: '…', other: '…' }) così `typeof en` vede PluralForms
// (e non il letterale a due categorie, che rifiuterebbe il «few» polacco);
// le locale tradotte, tipate `typeof en`, usano il letterale nudo.
export const plural = (forms: PluralForms): PluralForms => forms

const CATEGORIES: readonly string[] = ['zero', 'one', 'two', 'few', 'many', 'other']

export const isPluralForms = (value: unknown): value is PluralForms => {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return false
  }

  const entries = Object.entries(value)

  return (
    entries.length > 0 &&
    entries.every(([category, text]) => CATEGORIES.includes(category) && typeof text === 'string') &&
    'other' in value
  )
}

// Un'istanza di Intl.PluralRules per tag, riusata: la costruzione carica i
// dati CLDR della locale e t() gira dentro il render.
const rulesByTag = new Map<string, Intl.PluralRules>()

export const selectPlural = (bcp47Tag: string, forms: PluralForms, count: number | undefined): string => {
  if (count === undefined) {
    // Chiave plurale chiamata senza numero: l'«other» è l'unica forma sempre
    // presente — testo generico invece di un placeholder rotto a schermo.
    return forms.other
  }

  let rules = rulesByTag.get(bcp47Tag)

  if (rules === undefined) {
    rules = new Intl.PluralRules(bcp47Tag)
    rulesByTag.set(bcp47Tag, rules)
  }

  const text = forms[rules.select(count)] ?? forms.other

  return text.replaceAll('{n}', String(count)).replaceAll('{count}', String(count))
}
